import { Component, Inject, OnInit } from '@angular/core';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';

import { combineLatest, filter, take } from 'rxjs';

import { default as build } from 'src/app/build.json';

import { AdobeAnalyticsService } from 'src/app/modules/core/services/adobe-analytics.service';
import { ApplicationInsightsService } from 'src/app/modules/core/services/telemetry.service';
import { AuthService } from 'src/app/modules/core/services/auth.service';
import { BrandService } from 'src/app/modules/core/services/brand.service';
import { DialogService } from 'src/app/modules/core/services/dialog.service';
import { IdleService } from 'src/app/modules/core/services/idle-service.service';
import { ModalComponent } from 'src/app/modules/core/components/modal/modal.component';
import { SettingsService } from 'src/app/modules/core/services/settings.service';
import { TermsOfUseModalComponent } from 'src/app/modules/core/components/terms-of-use-modal/terms-of-use-modal.component';

import { AppFacade } from 'src/app/modules/ngrx-store/app/app.facade';
import { TermsAndConditionsFacade } from 'src/app/modules/ngrx-store/terms-and-conditions/terms-and-conditions.facade';
import { YourHomeFacade } from 'src/app/modules/ngrx-store/your-home/your-home.facade';

import { BuildVersionComponent } from 'src/app/modules/shared/components/build-version/build-version.component';
import { getAppLoadEvars, getAppLoadProps } from 'src/app/modules/shared/utilities/adobe-analytics.utils';
import { UnsubscribeOnDestroy } from 'src/app/modules/shared/utilities/unsubscribe-on-destroy';
import { getPageNameFromUrl } from 'src/app/modules/shared/utilities/page-name.utils';
import { NAVIGATION_ROUTES, REALTOR_ROUTES } from 'src/app/modules/shared/constants/route-paths';

@Component({
	selector: 'cp-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	imports: [CommonModule, RouterOutlet, BuildVersionComponent]
})
export class AppComponent extends UnsubscribeOnDestroy implements OnInit {
	private appInsights: ApplicationInsightsService;

	private brandName: string;

	private brandTitle: string;

	private termsOfUseModal?: MatDialogRef<unknown>;

	private logoutModal?: MatDialogRef<unknown>;

	/**
	 * Tracks the last sales agreement number for which App Initialization was fired.
	 * Prevents duplicate events when other observables re-emit, and resets when a
	 * realtor returns to the Client Management page so the next home selection
	 * always fires a fresh App Initialization event.
	 */
	private lastAppInitSag: string | undefined;

	myAccountUrl: string | undefined;

	isPreview?: boolean;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	build = (build as any).default;

	isProduction: boolean = true; // Default to true so we hide the branch indicator until Production value is determined

	idleMinutes: number = 14; // Default idle time in minutes
	
	constructor(
		private authService: AuthService,
		private brandService: BrandService,
		private dialogService: DialogService,
		@Inject(DomSanitizer) private domSanitizer: DomSanitizer,
		private matIconRegistry: MatIconRegistry,
		private router: Router,
		private settingsService: SettingsService,
		private appFacade: AppFacade,
		private yourHomeFacade: YourHomeFacade,
		private termsAndConditionsFacade: TermsAndConditionsFacade,
		private idle: Idle,
		private idleService: IdleService,
		private adobeAnalyticsService: AdobeAnalyticsService,
		private title: Title
	) {
		super();

		this.appInsights = new ApplicationInsightsService(this.router, this.authService);
		this.brandName = this.brandService.getBrand().name;
		this.brandTitle = this.brandService.getBrand().title;
	}

	ngOnInit(): void {
		// Only inject Adobe script once after settings are loaded
		this.settingsService.getSettings().pipe(
			this.takeUntilDestroyed()
		).subscribe(settings => {
			this.injectAdobeScript(settings.adobeScriptUrl);
			this.injectQuantumMetricScript(settings.quantumMetricScriptUrl);
			this.isProduction = settings.production;
		});

		// Combine router events with settings observable for route-based logic
		combineLatest([
			this.router.events.pipe(
				this.takeUntilDestroyed(),
				filter(event => event instanceof NavigationEnd)
			),
			this.settingsService.getSettings()
		]).pipe(
			this.takeUntilDestroyed()
		).subscribe(([routerEvent, settings]) => {
			if (routerEvent instanceof NavigationEnd) {
				const brand = this.brandService.getBrand();

				this.isPreview = this.authService.isPreviewUser();

				// Reset the App Init tracker when a realtor returns to the Client Management
				// page so that clicking into any home again fires a fresh App Initialization.
				const realtorHomesPath = `/${NAVIGATION_ROUTES.Realtor}/${REALTOR_ROUTES.Homes}`;
				if (routerEvent.urlAfterRedirects.startsWith(realtorHomesPath)) {
					this.lastAppInitSag = undefined;
				}

				if (!this.isPreview) {
					// Set myAccountUrl for non-preview route
					this.myAccountUrl = settings.brandConfigs[brand.name].myAccountUrl;

					this.idleMinutes = settings.portalTimeoutInMinutes || this.idleMinutes;

					// Set up idle check for non-preview route
					this.setupIdleCheck();
				}

				// Set title based on current page name and brand
				const pageName = getPageNameFromUrl(this.router.url);

				this.title.setTitle(`${ pageName } | ${ brand.accessibilityTitle } Portal`)

			}
		});

		// Load terms and conditions once when userInfo is available
		this.appFacade.userInfo$.pipe(
			filter(userInfo => !!userInfo),
			take(1)
		).subscribe(() => {
			this.termsAndConditionsFacade.loadTermsAndConditions();
		});

		this.appFacade.resetErrorCode();

		combineLatest([
			this.settingsService.getSettings(),
			this.appFacade.userInfo$,
			this.yourHomeFacade.homeDetails$,
			this.termsAndConditionsFacade.termsAndConditions$,
			this.termsAndConditionsFacade.loadTermsAndConditionsPending$,
		]).pipe(
			this.takeUntilDestroyed()
		).subscribe(
			async ([settings, userInfo, homeDetails, termsAndConditions, loadTermsAndConditionsPending]) => {
				if (settings && userInfo && homeDetails?.salesAgreementNumber) {
					// If user info has loaded see if terms of use need to be displayed
					if (!this.termsOfUseModal && loadTermsAndConditionsPending === false) {
						const termsAndConditionDate = termsAndConditions?.publishedDate; // Get from termsAndConditions facade
						// Check if terms of use have been acknowledged in current browser session
						const termsOfUseAcknowledged = termsAndConditionDate === settings.termsAndConditionDate;
						// If terms of use have not been acknowledged show modal
						this.termsOfUseModal = !termsOfUseAcknowledged || !termsAndConditions
							? this.dialogService.openDialog(TermsOfUseModalComponent, { disableClose: true, data: { termsAndConditions, userInfo, settings } })
							: this.termsOfUseModal;
					}

					// Only fire App Initialization when the selected home changes.
					// For realtors this fires each time a home is chosen from Client Management;
					// for all users it prevents duplicate events when other observables re-emit.
					if (homeDetails.salesAgreementNumber !== this.lastAppInitSag) {
						this.lastAppInitSag = homeDetails.salesAgreementNumber;

						// Initialize App Insights telemetry
						this.appInsights.initializeTelemetry(this.brandTitle, homeDetails, settings, userInfo);

						// Fetch the eVar and prop mappings for the app load event
						const evar = await getAppLoadEvars(userInfo, this.brandTitle, homeDetails);
						const prop = getAppLoadProps();
						
						// Track the app initialization in Adobe Analytics
						this.adobeAnalyticsService.trackEvent('App Initialization', evar, prop);

						// Mark app load as tracked
						this.appFacade.setAppLoadTracked(true);
					}
				}
			}
		);

		this.brandService.applyBrandTheme();
		this.registerCustomSVGIcons();

		this.idleService.modalEvent.subscribe(() => {
			// Reset idle timer when user interacts with in app modal unless the modal is the logout modal
			if (this.logoutModal) {
				return;
			}
			this.idle.interrupt(); // Reset idle timer when user interacts with in app modal
		});
	}

	// Script to inject Adobe script into the document head
	// eslint-disable-next-line class-methods-use-this
	private injectAdobeScript(adobeScriptUrl: string): void {
		if (!document.querySelector(`script[src='${adobeScriptUrl}']`)) {
			const adobeScript = document.createElement('script');
			adobeScript.src = adobeScriptUrl;
			adobeScript.async = true; // Ensure script loads asynchronously
			document.head.appendChild(adobeScript);
		}
	}

	// Script to inject Quantum Metric script into the document head
	// eslint-disable-next-line class-methods-use-this
	private injectQuantumMetricScript(quantumMetricScriptUrl: string): void {
		if (!document.querySelector(`script[src='${quantumMetricScriptUrl}']`)) {
			const quantumMetricScript = document.createElement('script');
			quantumMetricScript.src = quantumMetricScriptUrl;
			quantumMetricScript.async = true;
			document.head.appendChild(quantumMetricScript);
		}
	}

	setupIdleCheck() {
		// Set idle time to 14 minutes. After this time of inactivity, the warning dialog will be shown.
		this.idle.setIdle(this.idleMinutes * 60);

		// Set timeout to 1 minute. 1 minute after the idle time, if the user still hasn't interacted, log them out.
		this.idle.setTimeout(60);
	
		this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

		this.idle.onTimeout.pipe(
			this.takeUntilDestroyed()
		).subscribe(() => {
			// Logout the user when the total timeout is reached
			this.logoutUser();
		});
	
		this.idle.onTimeoutWarning.pipe(
			this.takeUntilDestroyed()
		).subscribe(() => {
			// Show warning modal here, only if not already shown
			if (!this.logoutModal) {
				this.openLogoutDialog();

				// Temporarily disable interrupts to prevent mouse movements or other actions from resetting the timer
				this.idle.setInterrupts([]);
			}
		});

		this.idle.watch();
	}

	openLogoutDialog() {
		// Ensure dialog is only opened once
		if (!this.logoutModal) {
			this.logoutModal = this.dialogService.openDialog(ModalComponent, {
				data: {
					title: 'Are you still there?',
					content: 'You have been inactive for a while. For your security, you will be signed out in 1 minute.',
					primaryLabel: 'Log Out',
					primaryFunction: () => this.logoutUser(),
					secondaryLabel: 'Continue Session',
					secondaryFunction: () => {
						// Reset dialogRef to allow dialog to be shown again
						this.logoutModal = undefined;

						// Reset idle watch and allow dialog to be shown again
						this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
						this.idle.watch();
					}
				}
			});

			this.logoutModal.afterClosed().pipe(
				this.takeUntilDestroyed()
			).subscribe(() => {
				// Reset dialogRef to allow dialog to be shown again
				this.logoutModal = undefined;

				// Reset idle watch and allow dialog to be shown again
				this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
				this.idle.watch();
			});
		}
	}

	logoutUser() {
		// Take user to myAccount login page for non-preview route
		if (!this.isPreview && this.myAccountUrl) {
			this.authService.logout();
		}
	}

	// register custom svg icons for use in <mat-icon> components
	registerCustomSVGIcons(): void {
		/*
		 * Options Selections
		 */
		this.matIconRegistry
			.addSvgIcon('options_exterior', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/options-selections/exterior.svg'));
		
		this.matIconRegistry
			.addSvgIcon('options_rooms', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/options-selections/rooms.svg'));
		
		this.matIconRegistry
			.addSvgIcon('options_flooring', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/options-selections/flooring.svg'));
		
		this.matIconRegistry
			.addSvgIcon('options_finishes', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/options-selections/finishes.svg'));
		
		this.matIconRegistry
			.addSvgIcon('options_extras', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/options-selections/extras.svg'));

		/*
		 * Navigation Items
		 */
		this.matIconRegistry
			.addSvgIcon('nav_your_home', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/navigation/your-home.svg'));
		this.matIconRegistry
			.addSvgIcon('nav_financial_services', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/navigation/financial-services.svg'));
		this.matIconRegistry
			.addSvgIcon('nav_message_center', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/navigation/message-center.svg'));
		this.matIconRegistry
			.addSvgIcon('nav_house_chimney', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/navigation/house-chimney.svg'));

		/*
		 * SCAR Progress
		 */
		this.matIconRegistry
			.addSvgIcon('scar_introduction', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/scar/introduction.svg'));
		
		this.matIconRegistry
			.addSvgIcon('scar_foundation', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/scar/foundation.svg'));
		
		this.matIconRegistry
			.addSvgIcon('scar_drywall', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/scar/drywall.svg'));
		
		this.matIconRegistry
			.addSvgIcon('scar_complete', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/scar/complete.svg'));
		
		/*
		 * Footer
		 */
		this.matIconRegistry
			.addSvgIcon('footer_audioeye', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/footer/audioeye.svg'));
		
		this.matIconRegistry
			.addSvgIcon('footer_equal_housing', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/footer/equal-housing.svg'));
		
		/*
		 * Service Requests
		 */
		this.matIconRegistry
			.addSvgIcon('service_requests_photo', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/service-requests/photo.svg'));
		
		this.matIconRegistry
			.addSvgIcon('upload_image', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/service-requests/upload-image.svg'));

		this.matIconRegistry
			.addSvgIcon('warning', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/service-requests/warning.svg'));

		/*
		* Client Cards
		*/
		this.matIconRegistry
			.addSvgIcon('floor-plan', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/images/client-card/Floorplan.svg'));
	}
	
	// eslint-disable-next-line class-methods-use-this
	get branch(): string {
		return build.branch.split('/').slice(2).join('/');
	}

	// eslint-disable-next-line class-methods-use-this
	get version(): string {
		return build.version;
	}
}